import React, { useCallback, useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import type {
  FilterWrapperParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { useGridFilterDisplay } from 'ag-grid-react';
import { SearchField } from '@wk/components-v3-react16';
import { dashboardFilterAtom } from '../../filterAtom';
import styles from './JobIdFilter.module.scss';

interface JobIdFilterModel {
  jobId: string;
}

interface JobIdFilterProperties
  extends CustomFilterDisplayProps<JobIdFilterModel>,
    FilterWrapperParams {}

const JobIdFilter = (properties: JobIdFilterProperties) => {
  const { model, onStateChange, state } = properties;
  const setDashboardFilter = useSetAtom(dashboardFilterAtom);
  const inputReference = useRef<HTMLInputElement>(null);

  const afterGuiAttached = useCallback(
    (parameters?: IAfterGuiAttachedParams) => {
      if (!parameters || !parameters.suppressFocus) {
        inputReference.current?.focus();
      }
    },
    []
  );

  // Register filter handlers with the grid
  useGridFilterDisplay({
    afterGuiAttached,
  });

  // Update atom when model changes (Apply/Reset actions)
  const updateAtom = useCallback(
    (newModel: JobIdFilterModel | null) =>
      setDashboardFilter((previousFilter) => ({
        ...previousFilter,
        jobId: newModel?.jobId || null,
      })),
    [setDashboardFilter]
  );

  // Track the previous model to detect when AG Grid applies the filter
  const previousModelReference = useRef<JobIdFilterModel | null>(null);

  useEffect(() => {
    // Only update atom when model actually changes (when Apply or Reset is clicked)
    const currentModelJobId = model?.jobId || null;
    const previousModelJobId = previousModelReference.current?.jobId || null;

    if (currentModelJobId !== previousModelJobId) {
      updateAtom(model);
      previousModelReference.current = model;
    }
  }, [model, updateAtom]);

  const currentJobId = state.model?.jobId || '';

  const handleInput = (event: React.FormEvent) => {
    const { value } = event.target as HTMLInputElement;
    const newModel = value ? { jobId: value } : null;
    onStateChange({ model: newModel });
  };

  const handleClearInput = () => {
    onStateChange({ model: null });
  };

  const hasClearIcon = currentJobId.length > 0;

  return (
    <div className={styles.container}>
      <SearchField
        searchButtonId="job-id-search-button"
        size="large"
        hasClearIcon={hasClearIcon}
        onClear={handleClearInput}
      >
        <input
          ref={inputReference}
          type="search"
          name="job-id-search"
          aria-labelledby="job-id-search-button"
          autoComplete="off"
          onInput={handleInput}
          placeholder="Search by Job ID..."
          value={currentJobId}
        />
      </SearchField>
    </div>
  );
};

export default JobIdFilter;
